import {
  stripeElementsModes,
  type StripeElementsMode,
} from "@/react/components/Paywalls/StripeElements/types";
import { paywallCurrencyCodes } from "@/react/types/PaywallCurrencyCodes";

/**
 * Determines the Stripe Elements mode for a given checkout scenario.
 *
 * Mode matrix:
 * - Zero amount (any): `setup`
 *   Covers free trials and 100%-off coupons. No payment collection; saves payment
 *   method for future charging.
 * - Recurring + BRL + non-zero: `subscription`
 *   PIX is BRL-only and does not support recurring payments. `subscription` mode
 *   causes Stripe to automatically filter PIX from the Payment Element.
 *   NOTE: `subscription` mode is more restrictive than `payment` mode for other
 *   methods (e.g., Afterpay, iDEAL, Bancontact), but those methods are not available
 *   in BRL, so there is no regression.
 * - Recurring + non-BRL + non-zero: `payment`
 *   PIX does not appear for non-BRL currencies regardless of mode. Using `payment`
 *   mode preserves all supported recurring payment methods (Afterpay for USD,
 *   iDEAL/Bancontact for EUR, etc.) which rely on the backend subscription creation
 *   pattern rather than Stripe's subscription mode filtering.
 * - One-time + non-zero: `payment`
 */
export const getStripeElementsMode = (
  isRecurring: boolean,
  amount: number,
  currency: string,
): StripeElementsMode => {
  if (amount <= 0) {
    return stripeElementsModes.setup;
  }

  if (isRecurring && currency.toLowerCase() === paywallCurrencyCodes.brl) {
    return stripeElementsModes.subscription;
  }

  return stripeElementsModes.payment;
};
